import React, { useState, useEffect } from 'react';
import { useTheme } from '../theme/ThemeContext';
import { getTimeSeriesSliderTheme } from '../theme/themeStyles';

interface TimeSeriesData {
  index?: string;
  interventionCompare?: {
    actionDate?: string;
    beforeIndex: number;
    afterIndex: number;
    nearCloud?: number | null;
    afterCloud?: number | null;
  };
  results: Array<{
    date: string;
    mean_index_value?: number;
    cloud_cover?: number;
    cloud_percentage?: number;
    tile_url?: string;
  }>;
}

function GuideClick({ isDark }: { isDark: boolean }) {
  return (
    <span
      className={`pointer-events-none absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full text-[9px] shadow ${
        isDark ? 'bg-[#008540] text-white' : 'bg-[#008540] text-white'
      }`}
      aria-hidden
    >
      <i className="ri-cursor-fill" />
    </span>
  );
}

function ChangeDetectionGuideFigure({ isDark }: { isDark: boolean }) {
  const chip = isDark
    ? 'border-white/15 bg-black/40 text-white'
    : 'border-[#e5e7eb] bg-white text-[#111827]';
  const quiet = isDark ? 'text-[#8b949e]' : 'text-[#5c6b62]';

  return (
    <div className="grid grid-cols-[1fr_auto_1fr_auto_1.15fr] items-center gap-1.5">
      <div className="min-w-0">
        <p className={`mb-1 text-[10px] font-medium ${quiet}`}>1. Click</p>
        <div className={`relative rounded-md border px-1.5 py-1 ${chip}`}>
          <GuideClick isDark={isDark} />
          <p className={`text-[9px] uppercase tracking-wide ${quiet}`}>Before</p>
          <p className="truncate text-[11px] font-semibold leading-tight">Earlier date</p>
        </div>
      </div>

      <i className={`ri-arrow-right-s-line text-sm ${quiet}`} aria-hidden />

      <div className="min-w-0">
        <p className={`mb-1 text-[10px] font-medium ${quiet}`}>2. Click</p>
        <div className={`relative rounded-md border px-1.5 py-1 ${chip}`}>
          <GuideClick isDark={isDark} />
          <p className={`text-[9px] uppercase tracking-wide ${quiet}`}>After</p>
          <p className="truncate text-[11px] font-semibold leading-tight">Later date</p>
        </div>
      </div>

      <i className={`ri-arrow-right-s-line text-sm ${quiet}`} aria-hidden />

      <div className="min-w-0">
        <p className={`mb-1 text-[10px] font-medium ${quiet}`}>3. Drag</p>
        <div
          className={`relative h-[38px] overflow-hidden rounded-md border ${isDark ? 'border-white/20' : 'border-[#c5d4b8]'}`}
          aria-hidden
        >
          <div className="absolute inset-y-0 left-0 w-1/2 bg-[#1f6b45]" />
          <div className="absolute inset-y-0 right-0 w-1/2 bg-[#d6e36a]" />
          <span className="absolute left-1 top-1 text-[8px] font-semibold uppercase tracking-wide text-white/90">
            Before
          </span>
          <span className="absolute right-1 top-1 text-[8px] font-semibold uppercase tracking-wide text-[#1c2b1e]">
            After
          </span>
          <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.25)]" />
          <div className="absolute left-1/2 top-1/2 flex h-4 w-4 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[10px] text-[#111827] shadow">
            <i className="ri-arrow-left-right-line" />
          </div>
        </div>
      </div>
    </div>
  );
}

interface TimeSeriesSliderProps {
  timeSeriesData: TimeSeriesData;
  currentIndex: number;
  isPlaying: boolean;
  playbackSpeed: number;
  onIndexChange: (index: number) => void;
  onPlayToggle: () => void;
  onSpeedChange: (speed: number) => void;
  onChangeDetectionToggle?: (enabled: boolean, beforeIndex?: number | null, afterIndex?: number | null) => void;
}

export default function TimeSeriesSlider({
  timeSeriesData,
  currentIndex,
  isPlaying,
  playbackSpeed,
  onIndexChange,
  onPlayToggle,
  onSpeedChange,
  onChangeDetectionToggle,
}: TimeSeriesSliderProps) {
  const { isDark } = useTheme();
  const ts = getTimeSeriesSliderTheme(isDark);

  const [isChangeDetectionMode, setIsChangeDetectionMode] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [beforeImageIndex, setBeforeImageIndex] = useState<number | null>(null);
  const [afterImageIndex, setAfterImageIndex] = useState<number | null>(null);
  const appliedAutoCompareKeyRef = React.useRef<string | null>(null);
  const onChangeDetectionToggleRef = React.useRef(onChangeDetectionToggle);
  onChangeDetectionToggleRef.current = onChangeDetectionToggle;

  useEffect(() => {
    return () => {
      onChangeDetectionToggleRef.current?.(false);
    };
  }, []);

  useEffect(() => {
    if (timeSeriesData?.results?.length) return;
    setIsChangeDetectionMode(false);
    setShowGuide(false);
    setBeforeImageIndex(null);
    setAfterImageIndex(null);
  }, [timeSeriesData]);

  const getCurrentImage = () => timeSeriesData?.results?.[currentIndex];

  const handleChangeDetectionToggle = () => {
    const newMode = !isChangeDetectionMode;
    setIsChangeDetectionMode(newMode);

    if (!newMode) {
      setShowGuide(false);
      setBeforeImageIndex(null);
      setAfterImageIndex(null);
      if (onChangeDetectionToggle) {
        onChangeDetectionToggle(false);
      }
    } else {
      setShowGuide(true);
      if (onChangeDetectionToggle) {
        onChangeDetectionToggle(true);
      }
    }
  };

  useEffect(() => {
    if (!isChangeDetectionMode) {
      setBeforeImageIndex(null);
      setAfterImageIndex(null);
    }
  }, [isChangeDetectionMode]);

  useEffect(() => {
    const cmp = timeSeriesData?.interventionCompare;
    const results = timeSeriesData?.results;
    if (!cmp || !results?.length) {
      appliedAutoCompareKeyRef.current = null;
      return;
    }
    const beforeIndex = cmp.beforeIndex;
    const afterIndex = cmp.afterIndex;
    if (
      beforeIndex == null ||
      afterIndex == null ||
      !results[beforeIndex] ||
      !results[afterIndex] ||
      beforeIndex === afterIndex
    ) {
      return;
    }
    const key = `${results[beforeIndex].date}|${results[afterIndex].date}|${beforeIndex}|${afterIndex}`;
    if (appliedAutoCompareKeyRef.current === key) return;
    appliedAutoCompareKeyRef.current = key;
    setIsChangeDetectionMode(true);
    setBeforeImageIndex(beforeIndex);
    setAfterImageIndex(afterIndex);
    onChangeDetectionToggleRef.current?.(true, beforeIndex, afterIndex);
  }, [timeSeriesData]);

  if (!timeSeriesData?.results || timeSeriesData.results.length === 0) {
    return null;
  }

  const beforeStartTimeMs =
    beforeImageIndex !== null
      ? new Date(timeSeriesData.results[beforeImageIndex].date).getTime()
      : null;

  const laterDateCount =
    beforeStartTimeMs === null
      ? 0
      : timeSeriesData.results.filter(
          (r) => new Date(r.date).getTime() > beforeStartTimeMs
        ).length;

  const afterSelectDisabled =
    beforeImageIndex === null || laterDateCount === 0;

  const progressPct =
    timeSeriesData.results.length > 1
      ? (currentIndex / (timeSeriesData.results.length - 1)) * 100
      : 0;

  const rangeStyle = {
    background: `linear-gradient(to right, ${ts.rangeFill} 0%, ${ts.rangeFill} ${progressPct}%, ${ts.rangeTrack} ${progressPct}%, ${ts.rangeTrack} 100%)`,
  };

  return (
    <div className={`absolute bottom-3 left-14 right-3 sm:left-[4.5rem] sm:right-[6rem] z-10 ${isChangeDetectionMode ? ts.shellCompact : ts.shell}`}>
      <div className={`flex items-center justify-between ${isChangeDetectionMode ? 'mb-1.5' : 'mb-3'}`}>
        <div className="flex items-center space-x-2 min-w-0">
          <h4 className={`${ts.title} flex items-center min-w-0`}>
            <i className={`ri-time-line mr-1.5 ${ts.titleIcon}`}></i>
            <span className="truncate">Time Series: {timeSeriesData.index?.toUpperCase()}</span>
            {isChangeDetectionMode && (
              <span className={ts.compareBadge}>Change Detection</span>
            )}
          </h4>
          <div className={ts.badge}>
            {currentIndex + 1} / {timeSeriesData.results.length}
          </div>
        </div>

        <div className="flex items-center space-x-1.5 flex-shrink-0">
          <button
            onClick={handleChangeDetectionToggle}
            className={`px-2 py-0.5 rounded text-xs flex items-center font-medium transition-colors ${
              isPlaying
                ? ts.compareDisabled
                : isChangeDetectionMode
                  ? ts.compareActive
                  : ts.compareIdle
            }`}
            disabled={isPlaying}
          >
            <i className="ri-contrast-2-line mr-1"></i>
            {isChangeDetectionMode ? 'Exit' : 'Change Detection'}
          </button>

          {!isChangeDetectionMode && (
            <>
              <button
                onClick={onPlayToggle}
                className={`px-3 py-1 rounded text-sm flex items-center font-medium transition-colors ${
                  isPlaying ? ts.playActive : ts.playIdle
                }`}
              >
                <i className={`ri-${isPlaying ? 'pause' : 'play'}-line mr-1`}></i>
                {isPlaying ? 'Pause' : 'Play'}
              </button>

              <select
                value={playbackSpeed}
                onChange={(e) => onSpeedChange(Number(e.target.value))}
                className={ts.speedSelect}
                disabled={isPlaying}
              >
                <option value={2000}>0.5x</option>
                <option value={1000}>1x</option>
                <option value={500}>2x</option>
                <option value={250}>4x</option>
              </select>
            </>
          )}
        </div>
      </div>

      {isChangeDetectionMode && showGuide && (
        <div className={`mb-2 rounded-lg border px-2.5 py-2 ${isDark ? 'border-white/10 bg-black/30' : 'border-[#e5e7eb] bg-white/80'}`}>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <p className={`text-[11px] font-semibold ${isDark ? 'text-white' : 'text-[#111827]'}`}>
              How to compare two dates
            </p>
            <button
              type="button"
              onClick={() => setShowGuide(false)}
              className={`shrink-0 text-[11px] font-medium ${isDark ? 'text-[#34d399]' : 'text-[#008540]'}`}
            >
              Got it
            </button>
          </div>
          <ChangeDetectionGuideFigure isDark={isDark} />
        </div>
      )}

      {isChangeDetectionMode && (
        <div className="mb-1.5">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={`${ts.beforeLabel} mb-0.5 block`}>
                {timeSeriesData.interventionCompare ? 'Near action' : 'Before'}
              </label>
              <select
                value={beforeImageIndex ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setBeforeImageIndex(null);
                    setAfterImageIndex(null);
                    if (onChangeDetectionToggle) {
                      onChangeDetectionToggle(true);
                    }
                    return;
                  }
                  const index = Number(raw);
                  setBeforeImageIndex(index);
                  setAfterImageIndex(null);
                  if (onChangeDetectionToggle) {
                    onChangeDetectionToggle(true, index, undefined);
                  }
                }}
                className={ts.beforeSelect}
              >
                <option value="">Select before date...</option>
                {timeSeriesData.results.map((result, index) => (
                  <option key={index} value={index}>
                    {new Date(result.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                    {result.cloud_percentage != null || result.cloud_cover != null
                      ? ` · ${Math.round(result.cloud_percentage ?? result.cloud_cover ?? 0)}% cloud`
                      : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                className={`mb-0.5 block ${
                  afterSelectDisabled ? ts.afterLabelDisabled : ts.afterLabel
                }`}
              >
                After
              </label>
              <select
                value={afterImageIndex ?? ''}
                disabled={afterSelectDisabled}
                onChange={(e) => {
                  if (beforeImageIndex === null || beforeStartTimeMs === null) return;
                  const raw = e.target.value;
                  if (raw === '') {
                    setAfterImageIndex(null);
                    if (onChangeDetectionToggle) {
                      onChangeDetectionToggle(true, beforeImageIndex, undefined);
                    }
                    return;
                  }
                  const index = Number(raw);
                  setAfterImageIndex(index);
                  if (onChangeDetectionToggle) {
                    onChangeDetectionToggle(true, beforeImageIndex, index);
                  }
                }}
                className={`w-full text-[11px] py-1 px-2 border rounded focus:outline-none focus:ring-1 focus:ring-gray-400 ${
                  afterSelectDisabled ? ts.afterSelectDisabled : ts.afterSelect
                }`}
              >
                <option value="">
                  {beforeImageIndex === null
                    ? 'Select before image first…'
                    : laterDateCount === 0
                      ? 'No later dates in this series'
                      : 'Select after date...'}
                </option>
                {timeSeriesData.results.map((result, index) => {
                  if (beforeStartTimeMs === null) return null;
                  if (new Date(result.date).getTime() <= beforeStartTimeMs) return null;
                  return (
                    <option key={index} value={index}>
                      {new Date(result.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                      {result.cloud_percentage != null || result.cloud_cover != null
                        ? ` · ${Math.round(result.cloud_percentage ?? result.cloud_cover ?? 0)}% cloud`
                        : ''}
                    </option>
                  );
                })}
              </select>
              {beforeImageIndex !== null && laterDateCount === 0 && (
                <p className={ts.hint}>
                  Choose an earlier start date to enable a later “after” image.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {!isChangeDetectionMode && (
        <div className="mb-3">
          <div className="relative">
            <input
              type="range"
              min="0"
              max={timeSeriesData.results.length - 1}
              value={currentIndex}
              onChange={(e) => onIndexChange(Number(e.target.value))}
              className={ts.rangeClass}
              style={rangeStyle}
            />

            <div className="absolute top-0 left-0 w-full h-3 pointer-events-none">
              {timeSeriesData.results.map((_, index) => (
                <div
                  key={index}
                  className={`${ts.marker} h-3`}
                  style={{
                    left: `${(index / (timeSeriesData.results.length - 1)) * 100}%`
                  }}
                />
              ))}
            </div>
          </div>

          <div className={`flex justify-between mt-2 ${ts.dateEdge}`}>
            <span>{new Date(timeSeriesData.results[0].date).toLocaleDateString()}</span>
            <span>{new Date(timeSeriesData.results[timeSeriesData.results.length - 1].date).toLocaleDateString()}</span>
          </div>
        </div>
      )}

      {!isChangeDetectionMode && getCurrentImage() && (
        <div className="grid grid-cols-4 gap-4 text-sm">
          <div className={ts.statBox}>
            <div className={ts.statLabel}>Date</div>
            <div className={ts.statValue}>
              {new Date(getCurrentImage()!.date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </div>
          </div>
          <div className={ts.statBoxIndex}>
            <div className={ts.statLabel}>Mean {timeSeriesData.index?.toUpperCase()}</div>
            <div className={ts.statValueIndex}>
              {getCurrentImage()?.mean_index_value?.toFixed(3) || 'N/A'}
            </div>
          </div>
          <div className={ts.statBoxProgress}>
            <div className={ts.statLabel}>Progress</div>
            <div className={ts.statValueProgress}>
              {`${Math.round(((currentIndex + 1) / timeSeriesData.results.length) * 100)}%`}
            </div>
          </div>
          <div className={ts.statBoxCloud}>
            <div className={ts.statLabel}>Cloud Cover</div>
            <div className={ts.statValueCloud}>
              {(() => {
                const imageData = getCurrentImage();
                const cloudValue = imageData?.cloud_percentage !== undefined ?
                  imageData.cloud_percentage : imageData?.cloud_cover;
                return cloudValue !== undefined ?
                  `${Math.round(cloudValue)}%` : 'N/A';
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
